import { Badge, Text } from "@microbeta/ui";
import type React from "react";
import { MdImage } from "react-icons/md";
import { Link } from "react-router-dom";
import type { ImageItem } from "../../../services/imageUtils";
import { ResponsiveImage } from "../ResponsiveImage/ResponsiveImage";
import styles from "./ContentCard.module.scss";

export interface ContentCardProps {
  title: string | React.ReactNode;
  description?: string;
  thumbnailUrl?: string;
  thumbnailVariants?: ImageItem;
  href: string;
  publishedAt?: string;
  className?: string;
  typeLabel?: string;
  typeLabelVariant?: "primary" | "secondary" | "success" | "warning" | "error";
  icon?: React.ReactNode;
  extraMeta?: React.ReactNode;
  bottomRightContent?: React.ReactNode;
  loading?: "lazy" | "eager";
  isBookmarked?: boolean;
}

export const ContentCard: React.FC<ContentCardProps> = ({
  title,
  description,
  thumbnailUrl,
  thumbnailVariants,
  href,
  publishedAt,
  className = "",
  typeLabel,
  typeLabelVariant = "primary",
  icon,
  extraMeta,
  bottomRightContent,
  loading = "lazy",
  isBookmarked,
}) => {
  return (
    <div className={`${styles.cardContainer} ${className}`}>
      <Link to={href} className={styles.cardLink}>
        <div
          className={`${styles.card} ${isBookmarked ? styles.bookmarked : ""}`}
        >
          <div className={styles.imageContainer}>
            {thumbnailVariants ? (
              <ResponsiveImage
                variants={thumbnailVariants}
                alt={String(title)}
                className={styles.thumbnail}
                loading={loading}
                sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 300px"
                size="small"
              />
            ) : thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={title}
                className={styles.thumbnail}
                loading={loading}
              />
            ) : (
              <div className={styles.imagePlaceholder}>
                {icon || <MdImage />}
              </div>
            )}
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardHeader}>
              {typeof title === "string" ? (
                <Text variant="large" className={styles.cardTitle}>
                  {title}
                </Text>
              ) : (
                <div className={styles.cardTitle}>{title}</div>
              )}
            </div>
            {extraMeta && <div className={styles.extraMeta}>{extraMeta}</div>}
            {description && (
              <Text variant="medium" className={styles.cardDescription}>
                {description}
              </Text>
            )}
            <div className={styles.cardBottom}>
              {typeLabel && (
                <Badge
                  className={styles.typeLabel}
                  variant={typeLabelVariant}
                  size="small"
                >
                  {typeLabel}
                </Badge>
              )}
              {bottomRightContent && (
                <div className={styles.bottomRightContent}>
                  {bottomRightContent}
                </div>
              )}
            </div>
            {publishedAt && (
              <Text variant="small" className={styles.cardMeta}>
                {new Date(publishedAt).toLocaleDateString()}
              </Text>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
};
